import React, {
    type Dispatch,
    type SetStateAction,
    useCallback, useMemo,
} from 'react';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {iterateLcdTreeCount} from 'src/utils/iterateLcdTreeCount';
import {getTreeStructure} from 'src/utils/mapTree';

import CheckBoxTree, {ChangeTreeType} from '@pb/kit-component-checkbox-tree';
import FormSection from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import {TreeStructure} from '@pb/kit-component-tree-v2';

import {LCD_EXPLANATIONS, LCD_HEADLINES} from './constants';

import type {LcdSubscriptions} from './typedef';
import type {DictionaryTree} from 'src/openapi/admin/models/DictionaryTree';
import type {Error} from 'src/openapi/admin/models/Error';
import type {RegionSettings} from 'src/openapi/admin/models/RegionSettings';


type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    trees: DictionaryTree[],
    subscriptions: Record<string, RegionSettings[]>,
    setSubscriptions: Dispatch<SetStateAction<Record<string, RegionSettings[]>>>,
    disabled?: boolean,
    onFieldChange: (parameterName: string) => void,
};

const getPublicationIds = (tree: DictionaryTree): string[] => (
    tree.children?.length ? tree.children.flatMap(getPublicationIds) : [tree.id]
);

export const LcdSubscriptionsView = (props: Props) => {
    const {
        isLoading,
        loadingError,
        subscriptions,
        setSubscriptions,
        trees,
        disabled,
        onFieldChange,
    } = props;

    const treeStructures = useMemo<Record<string, TreeStructure>>(() => {
        return trees.reduce((acc, tree) => ({
            ...acc,
            [tree.id]: getTreeStructure(tree, disabled).map(region => ({
                ...region,
                expanded: true,
            })),
        }), {} as Record<string, TreeStructure>);
    }, [trees, disabled]);

    const getSubscriptions = useCallback((subscriptionType: LcdSubscriptions, regionId: string) => {
        const subscriptionPack = subscriptions[subscriptionType];

        if (!subscriptionPack) {
            return [];
        }

        const region = subscriptionPack.find(e => e.region === regionId);

        if (region) {
            return region.subscriptions;
        } else {
            return [];
        }
    }, [subscriptions]);

    const getSubscriptionCount = useCallback((subscriptionType: string) => {
        const selected = subscriptions[subscriptionType];

        if (!selected) {
            return 0;
        }

        const count = selected.reduce((acc, item) => [
            ...acc,
            ...item.subscriptions.map(subscription => ({region: item.region, subscription})),
        ], [] as {region: string, subscription: string}[]);

        if (!count.length) {
            return 0;
        }

        const treeStructure = treeStructures[subscriptionType];

        const result: string[] = [];
        treeStructure.forEach(treeItem => iterateLcdTreeCount(
            treeItem,
            result,
            count,
            treeItem.id,
        ));

        return result.length;
    }, [subscriptions, treeStructures]);

    const onChange = useCallback((
        {checkedIdsList}: ChangeTreeType,
        subscriptionType: LcdSubscriptions,
        regionId: string,
    ) => {
        setSubscriptions(prevState => {
            const updatedRegions = [
                ...(prevState[subscriptionType] || []).filter(region => region.region !== regionId),
                {region: regionId, subscriptions: checkedIdsList},
            ];
            const regions = trees.find(tree => tree.id === subscriptionType)?.children || [];

            return {
                ...prevState,
                // Persist only publications from the current tree, not stale regions or parent IDs.
                [subscriptionType]: regions.flatMap(region => {
                    const publicationIds = new Set(getPublicationIds(region));
                    const publicationCodes = updatedRegions.find(item => item.region === region.id)
                        ?.subscriptions.filter(id => publicationIds.has(id)) || [];

                    return publicationCodes.length ? [{region: region.id, subscriptions: publicationCodes}] : [];
                }),
            };
        });
        onFieldChange(LCD_HEADLINES[subscriptionType]);
    }, [setSubscriptions, onFieldChange, trees]);


    if (loadingError?.status === 400) {
        return (
            <ErrorPage
                title="The LCD Credit Analysis is unavailable for untracked users"
                subtitle="Please contact your manager."
                small={true}
            />
        );
    }

    return (
        <Skeleton shown={isLoading} view={<div/>}>
            <ServerResponseHandler
                serverError={loadingError}
                smallView={true}
                sectionName="LCD Credit Analysis">
                <Group direction="v">
                    {Object.keys(treeStructures)
                        .sort((a, b) => a.localeCompare(b))
                        .map(key => (
                            <FormSection key={key} headline={`${LCD_HEADLINES[key]} (${getSubscriptionCount(key)})`}>
                                <Group direction="v" alignItems="start">
                                    <p>{LCD_EXPLANATIONS[key]}</p>
                                    <Group key={key} justify="space-between">
                                        {treeStructures[key].map(region => (
                                            <div style={{width: 300}} key={region.id}>
                                                <CheckBoxTree
                                                    data={[region]}
                                                    checkedIdsList={getSubscriptions(
                                                        key as LcdSubscriptions,
                                                        region.id,
                                                    )}
                                                    onChange={value => onChange(
                                                        value,
                                                        key as LcdSubscriptions,
                                                        region.id,
                                                    )}
                                                />
                                            </div>
                                        ))}
                                    </Group>
                                </Group>
                            </FormSection>
                        ))}
                </Group>
            </ServerResponseHandler>
        </Skeleton>
    );
};
